import { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Segmented } from "@/components/ui/segmented";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import { affectsLabel, canOverrideAt, inheritanceStatus, scopeNoun, scopeOfMeta } from "@/components/admin/adminScope";
import OrderTypesPicker from "@/components/admin/OrderTypesPicker";
import { CUSTOM_CONTROLS } from "@/components/admin/SettingControls";

/**
 * One setting, one row (§21):
 *
 *     Show discount                                   [ ON ]
 *     Controls whether the discount line appears on the bill
 *     Set at this outlet · Affects Bill · Financial
 *
 * The row is draft-driven: every edit lands in the page's staged draft map and
 * nothing saves until the owner commits the batch. There is deliberately no
 * per-row Save — per-field writes let each keystroke mutate live configuration
 * and made cross-setting mistakes untraceable.
 *
 * Danger classes get a named badge and an explicit reason:
 *   · FINANCIAL  — money: rates, charges, fiscal identity
 *   · restart    — requires_restart / requires_reload from the registry
 */

/** Compare a resolved value to a dependency target. Handles string/boolean mismatch. */
const settingMatches = (actual, expected) => {
  if (actual === expected) return true;
  return String(actual).toLowerCase() === String(expected).toLowerCase();
};

const DANGER_COPY = {
  financial: "Money — changes how bills are calculated",
  restart: "Takes effect after the API service restarts",
  reload: "Takes effect when workers next reload their config",
};

export default function SettingRow({
  meta,
  value,
  dirty,
  allSettings,
  onChange,
  readOnly,
  focused,
  onTouched,
  showRawIds,
  error,
}) {
  const anchor = useRef(null);

  // The outline says "this is the row you jumped to", so the row brings itself
  // into view instead of leaving the owner to scan the page for its own marker.
  useEffect(() => {
    if (focused && anchor.current?.scrollIntoView) anchor.current.scrollIntoView({ block: "center" });
  }, [focused]);

  // Touching the row means it has been found; the outline stops meaning anything
  // after that and must not mark one control for the rest of the session.
  const edit = next => { onChange(meta.key, next); onTouched?.(meta.key); };

  let visible = true;
  let dependencyLocked = false;
  for (const [key, value] of Object.entries(meta.visible_when || {})) {
    if (!settingMatches(allSettings?.[key]?.value, value)) visible = false;
  }
  for (const [key, value] of Object.entries(meta.disabled_when || {})) {
    if (settingMatches(allSettings?.[key]?.value, value)) dependencyLocked = true;
  }
  if (!visible) return null;

  // The registry declares the deepest scope a key resolves at. Offering a write
  // below it would store a value no consumer ever reads back.
  const overridable = canOverrideAt(meta, "GLOBAL");
  const locked = dependencyLocked || readOnly || !overridable;

  const status = inheritanceStatus(
    { source_scope: allSettings?.[meta.key]?.source_scope, overridden: allSettings?.[meta.key]?.overridden },
    "GLOBAL",
    "This Outlet"
  );
  const affects = affectsLabel(meta.affected_outputs);

  const dangers = [];
  if (meta.config_class === "financial") dangers.push({ kind: "financial", text: DANGER_COPY.financial });
  if (meta.requires_restart) dangers.push({ kind: "restart", text: DANGER_COPY.restart });
  else if (meta.requires_reload) dangers.push({ kind: "reload", text: DANGER_COPY.reload });

  const control = () => {
    // A value whose meaning is a time, hour, printer, schedule or image gets the
    // control for that meaning (SettingControls.jsx); it writes the same value.
    const Custom = CUSTOM_CONTROLS[meta.key];
    if (Custom) return <Custom meta={meta} value={value} onChange={edit} disabled={locked} />;
    if (meta.type === "boolean") {
      return (
        <Switch
          checked={String(value) === "true" || value === true}
          onCheckedChange={edit}
          disabled={locked}
          data-testid={`setting-${meta.key}-switch`}
        />
      );
    }
    // CSV-of-rows keys get their own picker — free text here can 400 order creation.
    if (meta.key === "order_types_enabled") {
      return (
        <OrderTypesPicker value={value ?? ""} onChange={edit} disabled={locked} />
      );
    }
    // An enumerated value never gets a free-text box — a typo on a fixed vocabulary
    // silently changes behavior. DESIGN.md §5.1: up to six options are all shown at
    // once (one click, nothing hidden); longer lists use the shared Select.
    if (meta.allowed_values?.length) {
      // The registry names each value for the owner ("Master group", not "group").
      const options = meta.allowed_values.map(option => ({
        value: String(option),
        label: meta.allowed_labels?.[String(option)] ?? String(option),
      }));
      if (options.length <= 6) {
        return (
          <Segmented
            value={value == null ? "" : String(value)}
            onValueChange={edit}
            options={options}
            disabled={locked}
            aria-label={meta.display_name}
            data-testid={`setting-${meta.key}-select`}
            itemTestIdPrefix={`setting-${meta.key}-option`}
          />
        );
      }
      return (
        <Select value={value == null ? "" : String(value)} onValueChange={edit} disabled={locked}>
          <SelectTrigger className="h-8 w-48 text-body" aria-label={meta.display_name} data-testid={`setting-${meta.key}-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        type={meta.type === "number" ? "number" : "text"}
        value={value ?? ""}
        onChange={event => edit(event.target.value)}
        className="h-8 w-48 text-body"
        disabled={locked}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `setting-${meta.key}-error` : undefined}
        data-testid={`setting-${meta.key}-input`}
      />
    );
  };

  return (
    <div
      className={`px-3 py-2.5 border-b last:border-b-0 ${dependencyLocked ? "opacity-60" : ""}${focused ? " search-hit" : ""}`}
      data-testid={`setting-${meta.key}-row`}
      ref={anchor}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-body font-medium leading-tight">{meta.display_name}</span>
            {dirty && (
              <span
                className="rounded-sm bg-warning-subtle px-1.5 py-px text-micro font-medium text-warning"
                data-testid={`setting-${meta.key}-dirty-badge`}
              >
                Unsaved
              </span>
            )}
            {dangers.map(danger => (
              <span
                key={danger.kind}
                title={danger.text}
                data-testid={`setting-${meta.key}-danger-badge`}
                className={
                  danger.kind === "financial"
                    ? "inline-flex items-center gap-0.5 rounded-sm bg-danger-subtle px-1.5 py-px text-micro font-medium text-danger"
                    : "inline-flex items-center gap-0.5 rounded-sm bg-warning-subtle px-1.5 py-px text-micro font-medium text-warning"
                }
              >
                <AlertTriangle className="w-2.5 h-2.5 shrink-0" aria-hidden="true" />
                {danger.kind === "financial" ? "Financial" : danger.kind === "restart" ? "Restart" : "Reload"}
              </span>
            ))}
          </div>
          <div className="text-meta text-muted-foreground mt-0.5">{meta.description}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {control()}
          {/* DESIGN.md §5.4: the error sits under the field it belongs to. */}
          {error && (
            <p id={`setting-${meta.key}-error`} className="max-w-[16rem] text-right text-meta text-danger" data-testid={`setting-${meta.key}-error`}>{error}</p>
          )}
        </div>
      </div>

      {/* D3 (DESIGN.md): captions stay muted but never lighter than AA — font-medium keeps 11px legible. */}
      <div
        className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-meta text-muted-foreground"
        data-testid={`setting-${meta.key}-status`}
      >
        <span>{status.text}</span>
        {affects && <span>· Affects {affects}</span>}
        {!overridable && (
          <span>· Not overridable per {scopeNoun("GLOBAL")} — this setting resolves at the {scopeOfMeta(meta) === "GLOBAL" ? "server" : "outlet"}</span>
        )}
        {dangers.map(danger => (
          <span key={`${danger.kind}-copy`} data-testid={`setting-${meta.key}-danger-note`} className="text-warning">
            · {danger.text}
          </span>
        ))}
        {showRawIds && (
          <code className="ml-auto font-mono text-micro opacity-70">{meta.key}</code>
        )}
      </div>
    </div>
  );
}
